/** Web Mapbox route map. Native resolves TourRouteMap.tsx. */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Text, View } from 'react-native'
import Map, { Layer, Marker, Source, type MapRef } from 'react-map-gl/mapbox'
import { useTranslation } from 'react-i18next'
import { useReducedMotion } from 'react-native-reanimated'
import { Navigation } from '@/components/ui/icons'
import { useTheme } from '@/lib/hooks/use-theme'
import { DURATION, useSemanticColors } from '@/lib/theme/tokens'
import { COLORS, FONTS, RADIUS, SPACING } from '@/lib/brand'
import { WEB_MAP_CANVAS_STYLE, WEB_MAP_SCROLL_ZOOM } from '@/lib/maps/web-map-gestures'
import { useLockPageScrollOverMap } from '@/lib/maps/use-web-map-wheel-lock'
import {
  applyMapboxWebConfig,
  ensureMapboxWeb,
  hasMapboxWebToken,
  MAPBOX_WEB_STYLE_URL,
  MAPBOX_WEB_TOKEN,
} from '@/lib/maps/mapbox-web'
import { layoutTourStopMarkers } from '@/lib/maps/tour-stop-layout'
import { TOUR_MAP_FIT_PADDING, TOUR_MAP_HEIGHT_PHONE } from '@/lib/maps/tour-map-layout'
import { MAPBOX_ROUTE_PITCH } from '@/lib/maps/mapbox-standard'
import { captureError } from '@/lib/utils/observability'
import { buildTourMapGeometry } from './tour-route-geometry'
import { useTourDrivingRoute } from './use-tour-driving-route'
import { TourStopMarker } from './TourStopMarker'
import { TourRouteMapChrome } from './TourRouteMapChrome'
import { TourRouteMapFallback } from './TourRouteMapFallback'
import type { TourRouteMapProps } from './tour-route-map.types'

ensureMapboxWeb()

const LOAD_GRACE_MS = 8000

export function TourRouteMap({
  stops,
  latestLocation,
  routeGeojson,
  testID,
  canvasHeight = TOUR_MAP_HEIGHT_PHONE,
  selectedStopId = null,
  onStopPress,
}: TourRouteMapProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const reducedMotion = useReducedMotion() === true
  const hostRef = useRef<View>(null)
  const mapRef = useRef<MapRef>(null)
  const flownStopRef = useRef<string | null>(null)
  const failTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading')
  const [attempt, setAttempt] = useState(0)
  useLockPageScrollOverMap(hostRef)
  const [mapWidth, setMapWidth] = useState(640)
  const driving = useTourDrivingRoute(stops, routeGeojson)
  const geometry = useMemo(
    () => buildTourMapGeometry(stops, latestLocation, driving.routeGeojson),
    [driving.routeGeojson, latestLocation, stops],
  )
  const placedStops = useMemo(
    () =>
      geometry
        ? layoutTourStopMarkers(stops, geometry.bounds, {
            width: mapWidth,
            height: canvasHeight,
            padding: TOUR_MAP_FIT_PADDING,
          })
        : [],
    [canvasHeight, geometry, mapWidth, stops],
  )

  const clearFailTimer = useCallback(() => {
    if (failTimer.current) {
      clearTimeout(failTimer.current)
      failTimer.current = null
    }
  }, [])

  useEffect(() => clearFailTimer, [clearFailTimer])

  useEffect(() => {
    clearFailTimer()
    setStatus('loading')
    failTimer.current = setTimeout(() => {
      failTimer.current = null
      setStatus((current) => {
        if (current === 'ready') return current
        captureError(new Error('Tour Mapbox failed to load its style or tiles'), {
          screen: 'tour-route-map-web',
          step: 'map_load_grace',
        })
        return 'failed'
      })
    }, LOAD_GRACE_MS)
    return clearFailTimer
  }, [attempt, clearFailTimer, isDark])

  useEffect(() => {
    if (!selectedStopId) return
    if (flownStopRef.current === selectedStopId) return
    const stop = placedStops.find((item) => item.id === selectedStopId)
    if (!stop) return
    if (!mapRef.current) return
    flownStopRef.current = selectedStopId
    mapRef.current.flyTo({
      center: [stop.longitude, stop.latitude],
      duration: reducedMotion ? 0 : DURATION.normal,
      essential: true,
    })
  }, [placedStops, reducedMotion, selectedStopId])

  const handleMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap()
    // Misma configuracion Standard que el nativo para la presentacion de ruta.
    if (map) applyMapboxWebConfig(map, 'route', isDark)
    clearFailTimer()
    setStatus('ready')
  }, [clearFailTimer, isDark])

  const handleMapError = useCallback(() => {
    clearFailTimer()
    setStatus((current) => {
      if (current === 'ready') return current
      captureError(new Error('Tour Mapbox reported a map error'), {
        screen: 'tour-route-map-web',
        step: 'map_error',
      })
      return 'failed'
    })
  }, [clearFailTimer])

  const handleRetry = useCallback(() => {
    clearFailTimer()
    setStatus('loading')
    setAttempt((current) => current + 1)
  }, [clearFailTimer])

  if (!hasMapboxWebToken() || !geometry) {
    return (
      <TourRouteMapFallback
        colors={colors}
        height={canvasHeight}
        testID={testID}
        title={t('tours.trip.map_unavailable')}
      />
    )
  }

  if (status === 'failed') {
    return (
      <TourRouteMapFallback
        colors={colors}
        height={canvasHeight}
        testID={testID}
        title={t('tours.map.load_error')}
        onRetry={handleRetry}
      />
    )
  }

  return (
    <View>
      <View
        ref={hostRef}
        style={{
          height: canvasHeight,
          overflow: 'hidden',
          borderRadius: RADIUS.xl,
          borderWidth: 1,
          borderColor: colors.border,
          backgroundColor: colors.surfaceSubtle,
        }}
        testID={testID}
        onLayout={(event) => {
          const next = Math.round(event.nativeEvent.layout.width)
          if (next > 0 && next !== mapWidth) setMapWidth(next)
        }}
      >
        <Map
          key={attempt}
          ref={mapRef}
          initialViewState={{
            longitude: geometry.center[0],
            latitude: geometry.center[1],
            zoom: 9,
            bounds: [geometry.bounds.sw, geometry.bounds.ne],
            fitBoundsOptions: { padding: TOUR_MAP_FIT_PADDING },
          }}
          mapboxAccessToken={MAPBOX_WEB_TOKEN}
          mapStyle={MAPBOX_WEB_STYLE_URL}
          attributionControl={false}
          // La ruta usa el mismo pitch auditado que el mapa nativo.
          pitch={MAPBOX_ROUTE_PITCH}
          dragRotate={false}
          pitchWithRotate={false}
          dragPan
          touchZoomRotate
          scrollZoom={WEB_MAP_SCROLL_ZOOM}
          doubleClickZoom
          style={WEB_MAP_CANVAS_STYLE}
          onLoad={handleMapLoad}
          onError={handleMapError}
        >
          {geometry.line ? (
            <Source id="tour-route-line" type="geojson" data={geometry.line}>
              <Layer
                id="tour-route-line-casing"
                type="line"
                paint={{ 'line-color': colors.surface, 'line-width': 7 }}
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
              />
              <Layer
                id="tour-route-line-layer"
                type="line"
                paint={{ 'line-color': COLORS.purple.DEFAULT, 'line-width': 4 }}
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
              />
            </Source>
          ) : null}
          {placedStops.map((stop) => (
            <Marker
              key={stop.id}
              longitude={stop.longitude}
              latitude={stop.latitude}
              anchor="bottom"
              style={{ pointerEvents: onStopPress ? 'auto' : 'none' }}
            >
              <View
                style={{
                  transform: [{ translateX: stop.offsetX }, { translateY: stop.offsetY }],
                }}
              >
                <TourStopMarker
                  number={stop.number}
                  kind={stop.kind}
                  total={placedStops.length}
                  selected={stop.id === selectedStopId}
                  onPress={onStopPress ? () => onStopPress(stop.id) : undefined}
                  accessibilityLabel={t('tours.stop_marker', {
                    number: stop.number,
                    label: stop.label,
                  })}
                  testID={`tours.stop-marker-${stop.number}`}
                />
              </View>
            </Marker>
          ))}
          {latestLocation ? (
            <Marker
              latitude={latestLocation.latitude}
              longitude={latestLocation.longitude}
              anchor="center"
            >
              <View
                accessible
                accessibilityRole="image"
                accessibilityLabel={t('tours.map.live_location')}
                testID="tours.live-location"
                style={{
                  width: 38,
                  height: 38,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 19,
                  borderWidth: 3,
                  borderColor: colors.surface,
                  backgroundColor: COLORS.coral.DEFAULT,
                }}
              >
                <Navigation size={18} color="#FFFFFF" fill="#FFFFFF" />
              </View>
            </Marker>
          ) : null}
        </Map>
        <TourRouteMapChrome routeStatus={driving.status} colors={colors} />
      </View>
      <Text
        style={{
          marginTop: SPACING.xs,
          fontFamily: FONTS.body,
          fontSize: 12,
          color: colors.textMuted,
        }}
      >
        {t('tours.map.gesture_hint_web')}
      </Text>
    </View>
  )
}
